import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import useAuth from "../../hooks/useAuth";
import { canWriteToDrivers } from "../../utils/roles";
import { showToast } from "../../utils/showToast";
import WriteToDriverModal from "./WriteToDriverModal";

/**
 * Punto único de integración de "Escribir a repartidor" para las dos vistas de
 * la mesa de ayuda (/admin/helpdesk y /admin/helpdesk/incidencias).
 *
 * Devuelve `open` (undefined si el rol no puede escribir a repartidores, así
 * ConversationList no pinta el botón) y `modal` para renderizar en la página.
 *
 * Tras un envío OK refresca la bandeja y abre la conversación creada; si el
 * backend no devolvió `conversationId`, avisa y deja la bandeja refrescada.
 *
 * @param {{
 *   refreshList?: () => unknown,
 *   openConversation: (ref: {id: string, channel?: string}) => unknown,
 * }} params
 */
export default function useWriteToDriver({ refreshList, openConversation }) {
  const { t } = useTranslation();
  const { user } = useAuth() || {};
  const allowed = canWriteToDrivers(user);
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleSent = useCallback(
    async ({ conversationId }) => {
      try {
        await refreshList?.();
      } catch (err) {
        // El mensaje ya salió; que falle el refresco no debe ocultarlo.
        console.error("No se pudo refrescar la bandeja tras escribir al repartidor:", err);
      }
      if (conversationId) {
        showToast(t("Mensaje enviado"), true);
        openConversation({ id: conversationId, channel: "whatsapp" });
      } else {
        showToast(
          t("Mensaje enviado. La conversación aparecerá en la bandeja en unos momentos."),
          true,
        );
      }
    },
    [refreshList, openConversation, t],
  );

  return {
    open: allowed ? handleOpen : undefined,
    modal: allowed && open ? (
      <WriteToDriverModal open onClose={handleClose} onSent={handleSent} />
    ) : null,
  };
}
